<template>
	<RouterLink :to="`/battle/${uuid}`" custom v-slot="{ navigate }">
		<BaseCard
			class="space-y-4 px-2 py-4"
			@click="navigate"
			:class="{ 'opacity-60': status === 'finished' && !running && !notOpaque }"
		>
			<div
				class="text-label-tertiary flex items-center gap-1.5 px-2 font-medium uppercase"
			>
				<JackpotIcon
					v-if="gameMode === 'jackpot' && !modifiers.includes('crazy')"
					class="h-5 w-5 shrink-0 text-[#DD8833]"
				/>
				<CrazyIcon
					v-if="modifiers.includes('crazy')"
					class="h-5 w-5 shrink-0 text-[#B78EEF]"
				/>
				<div
					class="text-sm"
					:class="{
						'text-[#DD8833]':
							gameMode === 'jackpot' && !modifiers.includes('crazy'),
						'text-[#B78EEF]': modifiers.includes('crazy')
					}"
				>
					{{ mode }}
				</div>
				<div class="ml-auto flex items-center gap-1">
					<template v-for="(team, i) in teams">
						<div class="mx-1 text-xs" v-if="i > 0">vs</div>
						<template v-for="participant in team">
							<div v-if="participant.name" class="relative">
								<LazyImage
									:src="participant.avatar ? avatarSm(participant.avatar) : ''"
									:alt="participant.name"
									class="h-7 w-7 rounded-full transition-opacity duration-500"
									:class="{
										'opacity-30':
											participant.winner &&
											!participant.winner.wonCoins &&
											((running && winnerKnown) || status === 'finished')
									}"
								/>
								<Transition
									enterActiveClass="transition-all duration-500"
									enterFromClass="scale-150 opacity-0"
								>
									<EliminatedIcon
										v-if="
											participant.winner &&
											!participant.winner.wonCoins &&
											((running && winnerKnown) || status === 'finished')
										"
										class="text-label-tertiary absolute -top-0.5 -left-0.5 h-8 w-8"
									/>
								</Transition>
							</div>
							<button
								v-else
								type="button"
								class="from-fill-secondary h-7 w-7 rounded-lg bg-gradient-to-b to-[#262626] p-px"
								v-hover
								@click="navigate"
							>
								<div
									class="from-fill-tertiary/50 to-fill-tertiary h-full w-full rounded-[7px] bg-gradient-to-b p-px"
								>
									<div
										class="from-fill-secondary flex h-full w-full items-center justify-center rounded-[6px] bg-gradient-to-b to-[#262626]"
									>
										<PlusSmIcon class="h-5 w-5" />
									</div>
								</div>
							</button>
						</template>
					</template>
				</div>
			</div>
			<div class="relative w-full overflow-hidden rounded-[10px] bg-black">
				<div class="w-full snap-x snap-mandatory overflow-hidden" ref="scroll">
					<div class="flex w-fit gap-1 p-1">
						<div
							class="flex h-15 w-15 shrink-0 snap-center items-center justify-center rounded-md bg-[#181818] transition-opacity"
							v-for="(item, i) in cases"
							:class="{
								'opacity-30': currentRound > i && running,
								'opacity-60': currentRound < i && running,
								'opacity-100': currentRound === i && running
							}"
							:data-snap="i === currentRound && running"
						>
							<LazyImage
								:src="caseIcon(item, true)"
								:alt="item"
								class="aspect-square w-[75%]"
							/>
						</div>
						<div class="h-15 w-1/2 shrink-0" />
					</div>
				</div>
				<div
					class="absolute top-0 bottom-0 left-0 w-1 bg-linear-to-r from-black to-transparent"
				></div>
				<div
					class="absolute top-0 right-0 bottom-0 w-1 bg-linear-to-l from-black to-transparent"
				></div>
				<div class="absolute top-1 right-1 flex items-center gap-1">
					<div
						v-if="modifiers.includes('crazy')"
						class="flex h-6.5 w-6.5 items-center justify-center rounded-full bg-[#27242C] shadow"
					>
						<CrazyIcon class="h-5 w-5 text-[#B78EEF]" />
					</div>
					<div
						v-if="gameMode === 'jackpot'"
						class="flex h-6.5 w-6.5 items-center justify-center rounded-full bg-[#352D24] shadow"
					>
						<JackpotIcon class="h-5 w-5 text-[#DD8833]" />
					</div>
					<div
						v-if="modifiers.includes('fast')"
						class="flex h-6.5 w-6.5 items-center justify-center rounded-full bg-[#27242C] shadow"
					>
						<LightningOutlineIcon class="text-yellow h-5 w-5" />
					</div>
					<div
						class="text-label-tertiary bg-fill-secondary inline-flex h-6.5 items-center gap-1 rounded-full px-2 text-xs font-medium uppercase shadow"
					>
						<CaseIcon class="h-5 w-5" v-if="status !== 'waiting_for_block'" />
						<div>{{ roundsText }}</div>
					</div>
				</div>
			</div>
			<div class="flex items-center gap-3 px-2">
				<div class="flex-1">
					<div class="h-5">
						<CoinPrice size="md" class="gap-0.5!" :price="fundedPrice" />
					</div>
					<div
						class="text-label-tertiary mt-1 text-sm font-medium capitalize"
						v-if="!funding"
					>
						{{ t('cost') }}
					</div>
					<CoinPrice
						class="*:text-label-tertiary mt-1 gap-0.5! line-through"
						size="sm"
						:price="entryFee"
						disabled
						v-else
					/>
				</div>
				<BaseButton
					class="relative flex-1"
					size="md"
					:color="color"
					v-if="status === 'created'"
				>
					<div>{{ joinText }}</div>
					<PriceDiscount :discount="funding" rotated />
				</BaseButton>
				<BaseButton class="flex-1 uppercase" size="md" color="secondary" v-else>
					<div>{{ t('view') }}</div>
				</BaseButton>
			</div>
		</BaseCard>
	</RouterLink>
</template>
<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import BaseCard from './BaseCard.vue'
import LazyImage from './LazyImage.vue'
import PlusSmIcon from '@/assets/icons/plus-sm.svg'
import BaseButton from './BaseButton.vue'
import CrazyIcon from '@/assets/icons/crazy.svg'
import ViewIcon from '@/assets/icons/view.svg'
import { caseIcon, avatarSm } from '@/utils/urls'
import EliminatedIcon from '@/assets/icons/eliminated.svg'
import { sleep } from '@/utils/sleep'
import { useUser } from '@/stores/user'
import LightningOutlineIcon from '@/assets/icons/lightning-outline.svg'
import CoinPrice from './CoinPrice.vue'
import { useI18n } from 'vue-i18n'
import CaseIcon from '@/assets/icons/case.svg'
import PriceDiscount from './PriceDiscount.vue'
import JackpotIcon from '@/assets/icons/jackpot-sm.svg'

const { t } = useI18n({
	messages: {
		en: {
			view: 'view battle',
			join: 'join battle',
			open: 'open battle',
			status: {
				waiting: 'waiting...'
			},
			cost: 'battle cost'
		},
		ru: {
			view: 'посмотреть',
			join: 'вступить',
			open: 'открыть',
			status: {
				waiting: 'ожидание...'
			},
			cost: 'стоимость'
		}
	}
})

const props = defineProps<{
	uuid: string
	status: 'created' | 'running' | 'finished' | 'waiting_for_block'
	participants: {
		name: string | null
		avatar: string | null
		isBot: boolean | null
		teamIndex: number
		slotIndex: number
		winner: {
			wonCoins: number
		} | null
		me: boolean | null
	}[]
	cases: string[]
	entryFee: number
	gameMode: 'classic' | 'shared' | 'jackpot'
	modifiers: ('crazy' | 'fast')[]
	startedAt: number | null
	tieBreakWinner: number | null
	funding: number
	notOpaque?: boolean
}>()

const emit = defineEmits<{
	(
		e: 'setStatus',
		status: 'created' | 'running' | 'finished' | 'waiting_for_block'
	): void
}>()

const currentRound = ref(0)

const mode = computed(() => {
	if (props.gameMode === 'shared') {
		return 'shared'
	} else if (props.modifiers.includes('crazy')) {
		return 'crazy'
	} else if (props.gameMode === 'jackpot') {
		return 'jackpot'
	}
	return 'classic'
})

const color = computed(() => {
	if (props.gameMode === 'shared') {
		return 'green'
	} else if (props.modifiers.includes('crazy')) {
		return 'purple'
	}
	return 'primary'
})

const teams = computed(() => {
	return props.participants.reduce(
		(acc, participant) => {
			if (!acc[participant.teamIndex]) {
				acc[participant.teamIndex] = []
			}
			acc[participant.teamIndex].push(participant)
			return acc
		},
		[] as Array<typeof props.participants>
	)
})

const roundsText = computed(() => {
	if (running.value) {
		return `${currentRound.value + 1}/${props.cases.length}`
	}
	if (props.status === 'waiting_for_block') {
		return t('status.waiting')
	}

	return props.cases.length
})

const fundedPrice = computed(() =>
	props.funding
		? Math.ceil((props.entryFee * (100 - props.funding)) / 100)
		: props.entryFee
)

const isJoined = computed(() => props.participants.some((p) => p.me))

const joinText = computed(() => {
	if (isJoined.value) {
		return t('open')
	}

	return t('join')
})

const scroll = ref<HTMLDivElement>()

const scrollToCurrentRound = async () => {
	await nextTick()
	const snap = document.querySelector('[data-snap="true"]') as HTMLDivElement

	if (!snap || !scroll.value) {
		return
	}

	scroll.value.scrollTo({
		left: snap.offsetLeft - scroll.value.offsetWidth / 2,
		behavior: 'smooth'
	})
}

const running = ref(false)
const winnerKnown = ref(false)

const user = useUser()

const play = async () => {
	running.value = true

	await user.nowAvailable()

	const fast = props.modifiers.includes('fast')
	const diff = props.startedAt ? user.now() - props.startedAt : 0
	let totalDuration = 0

	for (let i = 0; i < props.cases.length; i++) {
		currentRound.value = i

		let roundDuration = 0

		if (fast) {
			roundDuration = 3210 + props.participants.length * 100
		} else {
			roundDuration = 5210 + props.participants.length * 300
		}

		totalDuration += roundDuration

		if (totalDuration < diff) {
			continue
		}

		await scrollToCurrentRound()

		await sleep(roundDuration)
	}

	const preTallyDuration =
		(fast ? 1500 : 3000) + props.participants.length * 300

	totalDuration += preTallyDuration

	if (totalDuration > diff) {
		await sleep(preTallyDuration)
	}

	if (props.tieBreakWinner !== null) {
		totalDuration += 3650
		if (totalDuration > diff) {
			await sleep(3650)
		}
	}
	if (props.gameMode === 'jackpot') {
		totalDuration += 4650
		if (totalDuration > diff) {
			await sleep(4650)
			winnerKnown.value = true
			await sleep(5000)
		} else {
			winnerKnown.value = true
		}
	} else {
		const postTallyDuration =
			(fast ? 1500 : 3000) + props.participants.length * 300 + 2000

		if (totalDuration > diff) {
			await sleep(postTallyDuration)
			winnerKnown.value = true
			await sleep(5000)
		} else {
			winnerKnown.value = true
		}
	}

	running.value = false

	emit('setStatus', 'finished')
}

watch(
	() => props.status,
	() => {
		if (props.status !== 'running') {
			return
		}

		play()
	},
	{ immediate: true }
)
</script>
